/**
 * Everything one role may reach, on one screen.
 *
 * The detail used to unfold under the table, which pushed the other roles off
 * the page and gave you no way to compare one against another — you scrolled
 * away from the thing you were checking it against. As a sheet it sits over
 * the table instead, and closing it puts you back exactly where you were.
 *
 * Read-only by design: changing permissions is four decisions in order, which
 * is what the wizard is for. The Edit button hands over to it.
 */
import { useApp } from '../../store/AppContext.jsx';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import { Avatar, Pill } from '../Primitives.jsx';

const VERB_LABEL = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete' };

/* The same colours the team table gives each role, so the two agree. */
const ROLE_TONE = {
  Owner: 'p-purple', Admin: 'p-blue', Manager: 'p-cyan', Agent: 'p-green', Viewer: 'p-grey'
};

export default function RoleSheet({ role, tree, policy, onClose, onEdit }) {
  const { toast } = useApp();
  const menus = tree.modules.flatMap(m => m.menus);
  const open = menus.filter(x => (policy[x.key] || []).includes('view'));

  const count = verb => menus.filter(x => (policy[x.key] || []).includes(verb)).length;

  const hero = (
    <div className="vhero">
      <div className="vhero-in">
        <Avatar name={role} className="vava" />
        <div className="vwho">
          <b>{role}</b>
          <span>
            {role === 'Owner'
              ? 'Always has everything — this one cannot be restricted'
              : `${open.length} of ${menus.length} menus`}
          </span>
        </div>
      </div>

      <div className="vstats">
        <div className="vstat"><span>{open.length}</span><small>Can open</small></div>
        <div className="vstat"><span>{count('create')}</span><small>Can create</small></div>
        <div className="vstat"><span>{count('edit')}</span><small>Can edit</small></div>
        <div className="vstat"><span>{count('delete')}</span><small>Can delete</small></div>
      </div>
    </div>
  );

  return (
    <Sheet
      icon="lock"
      tone="g-purple"
      title="Role permissions"
      badge={<Pill tone={ROLE_TONE[role] || 'p-grey'}>{role}</Pill>}
      onClose={onClose}
      closeLabel="Close"
      bodyClass="viewbody"
      wide
      hero={hero}
      footerLeft={
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            if (role === 'Owner') { toast('The Owner always has everything'); return; }
            onEdit(role);
          }}
          disabled={role === 'Owner'}
        >
          <Icon name="edit" />Edit permissions
        </button>
      }
    >
      {tree.modules.map(m => {
        const inModule = m.menus.filter(x => (policy[x.key] || []).includes('view'));
        return (
          <section className={`vpanel wide rsmod${inModule.length ? '' : ' shut'}`} key={m.key}>
            <h4>
              <span className={`ic ${inModule.length ? 'g-purple' : 'g-slate'}`}>
                <Icon name={inModule.length ? 'check' : 'lock'} />
              </span>
              {m.name}
              <span className="rscount">
                {inModule.length ? `${inModule.length} of ${m.menus.length}` : 'Hidden'}
              </span>
            </h4>

            <div className="vbody">
              {m.menus.map(menu => {
                const held = policy[menu.key] || [];
                return (
                  <div className={`rsrow${held.length ? '' : ' off'}`} key={menu.key}>
                    <span className="rsname">{menu.name}</span>
                    <span className="rsverbs">
                      {held.length ? (
                        /* Only the verbs this screen has: a dash for a delete
                           that does not exist would read as "denied". */
                        menu.verbs.map(v => (
                          <span key={v} className={`rsverb${held.includes(v) ? ' on' : ''}`}>
                            {held.includes(v) ? <Icon name="check" /> : null}
                            {VERB_LABEL[v]}
                          </span>
                        ))
                      ) : (
                        <i className="rsno">No access</i>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </Sheet>
  );
}
